import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { useSessionStore } from '@/stores/session';
import type { RoleRedirectState } from './roleRedirect';

/**
 * Guard для защищённых роутов.
 * Если пользователь не авторизован — редирект на /signin с сохранением текущего пути.
 * Если авторизован — рендерит вложенные роуты через Outlet.
 */
export function ProtectedRoute() {
	const isAuthorized = useSessionStore((s) => s.isAuthorized);
	const setPrevPath = useSessionStore((s) => s.setPrevPath);
	const location = useLocation();
	const currentPath = location.pathname + location.search;

	// Сохраняем путь, чтобы вернуться после входа. В эффекте, а не при рендере: на стор
	// сессии подписаны другие компоненты, и запись посреди рендера обновляла бы их
	useEffect(() => {
		if (!isAuthorized) setPrevPath(currentPath);
	}, [isAuthorized, currentPath, setPrevPath]);

	if (!isAuthorized) {
		return <Navigate to="/signin" replace />;
	}

	return <Outlet />;
}

/**
 * Guard для гостевых роутов (signin, signup и т.д.).
 * Если пользователь уже авторизован — редирект на prevPath или /overview.
 * Если не авторизован — рендерит вложенные роуты через Outlet.
 */
export function GuestRoute() {
	const isAuthorized = useSessionStore((s) => s.isAuthorized);
	const prevPath = useSessionStore((s) => s.prevPath);

	if (isAuthorized) {
		// Возвращаем на сохранённый путь или на главную
		const redirectTo = prevPath || '/overview';
		return <Navigate to={redirectTo} replace />;
	}

	return <Outlet />;
}

/**
 * Родительский адрес: последний сегмент пути отбрасывается.
 * /llm-gate/routes/5/edit -> /llm-gate/routes/5, /llm-gate/routes/new -> /llm-gate/routes.
 */
function parentPath(pathname: string): string {
	const trimmed = pathname.replace(/\/+$/, '');
	const parent = trimmed.slice(0, trimmed.lastIndexOf('/'));
	return parent || '/';
}

/**
 * Guard для страниц изменения, доступных только администратору.
 *
 * Бэкенд отвергает запись остальных ролей (403), и форма, которую нельзя
 * сохранить, только сбивала бы с толку. Прочих пользователей уводит на
 * родительский адрес: у страниц изменения это страница просмотра того же
 * объекта или список раздела. RequirePermission охраняет само чтение по capability:
 * безопасного родительского представления у такого отказа может не быть, поэтому он
 * показывает отказ на текущем адресе. Роль и право не подменяют друг друга.
 */
export function AdminRoute({ redirectTo }: { redirectTo?: string } = {}) {
	const isAdmin = useIsAdmin();
	const { pathname } = useLocation();

	if (!isAdmin) {
		const state: RoleRedirectState = { roleRedirectFrom: pathname };
		return <Navigate to={redirectTo ?? parentPath(pathname)} replace state={state} />;
	}

	return <Outlet />;
}
